import { Anima } from "kido/anima";

export interface MobileMediaItem {
  imageUrl: string | null;
  videoUrl: string | null;
  isVideo: boolean;
  playButton?: boolean;
}

export interface MobileProjectEntry {
  slug: string;
  title: string;
  client: string;
  year: string;
  summaryHtml: string;
  media: MobileMediaItem[];
}

export class ProjectMobilePreview {
  private root: HTMLElement;
  private scrim: HTMLElement;
  private modal: HTMLElement;
  private track: HTMLElement;
  private carousel: HTMLElement;
  private body: HTMLElement;
  private infoEl: HTMLElement;
  private cta: HTMLAnchorElement;
  private closeBtn: HTMLElement;
  private prevBtn: HTMLElement;
  private nextBtn: HTMLElement;

  private isOpen = false;
  private currentIndex = 0;
  private slideCount = 0;
  private slideWidth = 0;

  private basePath: string;
  private anchorSelector: string;

  // Listeners to remove on destroy
  private cardListeners: Array<{
    el: HTMLElement;
    handler: (e: Event) => void;
  }> = [];

  constructor(basePath = "/projects", anchorSelector = ".p-el_a") {
    this.basePath = basePath;
    this.anchorSelector = anchorSelector;
    // Root overlay
    this.root = document.createElement("div");
    this.root.className = "pm";

    // Scrim
    this.scrim = document.createElement("div");
    this.scrim.className = "pm_scrim";
    this.root.appendChild(this.scrim);

    // Modal
    this.modal = document.createElement("div");
    this.modal.className = "pm_modal";

    // Close button
    this.closeBtn = document.createElement("button");
    this.closeBtn.className = "pm_close";
    this.closeBtn.setAttribute("aria-label", "Close");
    this.closeBtn.innerHTML = `<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M1 1L13 13M13 1L1 13" stroke="currentColor" stroke-width="1.5"/></svg>`;
    this.modal.appendChild(this.closeBtn);

    // Carousel (position:relative so counter can overlay)
    this.carousel = document.createElement("div");
    this.carousel.className = "pm_carousel";
    this.track = document.createElement("div");
    this.track.className = "pm_carousel_track";
    this.carousel.appendChild(this.track);

    // Left/right click zones for prev/next
    const hitLeft = document.createElement("div");
    hitLeft.className = "pm_carousel_hit pm_carousel_hit--l";
    hitLeft.addEventListener("click", () =>
      this.goToSlide(this.currentIndex - 1)
    );
    this.carousel.appendChild(hitLeft);

    const hitRight = document.createElement("div");
    hitRight.className = "pm_carousel_hit pm_carousel_hit--r";
    hitRight.addEventListener("click", () =>
      this.goToSlide(this.currentIndex + 1)
    );
    this.carousel.appendChild(hitRight);

    // Nav buttons overlay bottom-right of carousel
    const nav = document.createElement("div");
    nav.className = "pm_nav";

    this.prevBtn = document.createElement("button");
    this.prevBtn.className = "pm_nav_a pm_nav_a--l";
    this.prevBtn.innerHTML = `<svg width="5" height="8" viewBox="0 0 6 10" fill="none"><path d="M5 1L1 5L5 9" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

    this.nextBtn = document.createElement("button");
    this.nextBtn.className = "pm_nav_a pm_nav_a--r";
    this.nextBtn.innerHTML = `<svg width="5" height="8" viewBox="0 0 6 10" fill="none"><path d="M1 1L5 5L1 9" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

    nav.appendChild(this.prevBtn);
    nav.appendChild(this.nextBtn);
    this.carousel.appendChild(nav);

    this.modal.appendChild(this.carousel);

    // Body
    this.body = document.createElement("div");
    this.body.className = "pm_body";

    // Info row: TITLE sticker + title value, CLIENT sticker + client value (inline)
    this.infoEl = document.createElement("div");
    this.infoEl.className = "pm_body_info";

    this.body.appendChild(this.infoEl);
    this.modal.appendChild(this.body);

    // Footer with CTA
    const foot = document.createElement("div");
    foot.className = "pm_foot";

    this.cta = document.createElement("a");
    this.cta.className = "pm_foot_cta";
    this.cta.textContent = "VIEW PROJECT";
    foot.appendChild(this.cta);
    this.modal.appendChild(foot);

    this.root.appendChild(this.modal);
    document.body.appendChild(this.root);

    // Wire close handlers
    this.scrim.addEventListener("click", () => this.close());
    this.closeBtn.addEventListener("click", () => this.close());
    this.prevBtn.addEventListener("click", () =>
      this.goToSlide(this.currentIndex - 1)
    );
    this.nextBtn.addEventListener("click", () =>
      this.goToSlide(this.currentIndex + 1)
    );
  }

  init(cards: HTMLElement[], data: MobileProjectEntry[]): void {
    for (const card of cards) {
      const anchor = card.querySelector(
        this.anchorSelector
      ) as HTMLElement | null;
      if (!anchor) continue;
      const slug = card.getAttribute("data-slug");
      if (!slug) continue;

      const entry = data.find((d) => d.slug === slug);
      if (!entry) continue;

      const handler = (e: Event) => {
        e.preventDefault();
        e.stopPropagation();
        this.open(entry);
      };

      anchor.addEventListener("click", handler);
      this.cardListeners.push({ el: anchor, handler });
    }
  }

  open(entry: MobileProjectEntry): void {
    if (this.isOpen) return;
    this.isOpen = true;

    // Build slides
    this.track.innerHTML = "";
    const usableMedia = entry.media.filter((m) => m.imageUrl || m.videoUrl);
    this.slideCount = usableMedia.length;
    this.currentIndex = 0;

    for (const item of usableMedia) {
      const slide = document.createElement("div");
      slide.className = "pm_slide";
      const fig = document.createElement("figure");

      if (item.isVideo && item.videoUrl) {
        const video = document.createElement("video");
        // Lazy: defer src until this slide becomes current
        video.dataset.src = item.videoUrl;
        video.playsInline = true;
        video.muted = true;
        video.loop = true;
        video.preload = "none";
        if (item.imageUrl) video.poster = item.imageUrl;
        fig.appendChild(video);

        if (item.playButton) {
          const playBtn = document.createElement("button");
          playBtn.className = "pm_play pj-play";
          playBtn.setAttribute("aria-label", "Play Video");
          playBtn.innerHTML = `<svg width="12" height="14" viewBox="0 0 12 14" fill="none"><path d="M1 1L11 7L1 13V1Z" fill="currentColor"/></svg>`;
          slide.appendChild(playBtn);
        }
      } else if (item.imageUrl) {
        const img = document.createElement("img");
        img.src = item.imageUrl;
        img.alt = entry.title;
        img.loading = "lazy";
        fig.appendChild(img);
      }

      slide.appendChild(fig);
      this.track.appendChild(slide);
    }

    // Set info — matches desktop .p-el_i layout: TITLE sticker + value, CLIENT sticker + value
    this.infoEl.innerHTML = "";
    if (entry.title) {
      this.infoEl.innerHTML += `<div class="pm_body_info_e"><div class="pm_body_info_label">TITLE</div><p>${entry.title}</p></div>`;
    }
    if (entry.client) {
      this.infoEl.innerHTML += `<div class="pm_body_info_e"><div class="pm_body_info_label">CLIENT</div><p>${entry.client}</p></div>`;
    }
    this.cta.href = `${this.basePath}/${entry.slug}`;

    // Measure slide width after DOM updates
    requestAnimationFrame(() => {
      const firstSlide = this.track.querySelector(
        ".pm_slide"
      ) as HTMLElement | null;
      this.slideWidth = firstSlide?.offsetWidth ?? this.modal.offsetWidth;
      this.track.style.transform = "translateX(0)";
    });

    // Show overlay with animation
    this.root.classList.add("is-open");

    const scrimAnim = new Anima({
      el: this.scrim,
      p: { o: [0, 1] },
      d: 300,
      e: "oQ",
    });
    scrimAnim.play();

    const modalAnim = new Anima({
      el: this.modal,
      p: { o: [0, 1], s: [0.95, 1] },
      d: 300,
      e: "oQ",
    });
    modalAnim.play();

    // Play first video if applicable
    this.playCurrentVideo();
  }

  close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;

    // Fade out
    const scrimAnim = new Anima({
      el: this.scrim,
      p: { o: [1, 0] },
      d: 250,
      e: "oQ",
    });
    scrimAnim.play();

    const modalAnim = new Anima({
      el: this.modal,
      p: { o: [1, 0], s: [1, 0.95] },
      d: 250,
      e: "oQ",
      cb: () => {
        this.root.classList.remove("is-open");
        this.pauseAllVideos();
        this.track.innerHTML = "";
        this.currentIndex = 0;
        this.slideCount = 0;
      },
    });
    modalAnim.play();
  }

  destroy(): void {
    // Remove card listeners
    for (const { el, handler } of this.cardListeners) {
      el.removeEventListener("click", handler);
    }
    this.cardListeners = [];

    this.pauseAllVideos();

    // Remove root from body
    this.root.remove();
  }

  private goToSlide(index: number): void {
    const clamped = Math.max(0, Math.min(index, this.slideCount - 1));
    if (clamped === this.currentIndex) return;

    this.pauseCurrentVideo();
    const startX = -this.currentIndex * this.slideWidth;
    this.currentIndex = clamped;
    const targetX = -clamped * this.slideWidth;
    const anim = new Anima({
      d: 300,
      e: "oQ",
      u: (v) => {
        const x = startX + (targetX - startX) * v.prE;
        this.track.style.transform = `translateX(${x}px)`;
      },
      cb: () => {
        this.track.style.transform = `translateX(${targetX}px)`;
        this.playCurrentVideo();
      },
    });
    anim.play();
  }

  private hydrateVideo(video: HTMLVideoElement): void {
    const ds = video.dataset.src;
    if (ds && !video.src) {
      video.src = ds;
      video.preload = "auto";
    }
  }

  private playCurrentVideo(): void {
    const slides = this.track.querySelectorAll(".pm_slide");
    const current = slides[this.currentIndex];
    if (!current) return;
    const video = current.querySelector("video") as HTMLVideoElement | null;
    if (video) {
      this.hydrateVideo(video);
      video.play().catch(() => {});
    }
  }

  private pauseCurrentVideo(): void {
    const slides = this.track.querySelectorAll(".pm_slide");
    const current = slides[this.currentIndex];
    if (!current) return;
    const video = current.querySelector("video") as HTMLVideoElement | null;
    if (video) video.pause();
  }

  private pauseAllVideos(): void {
    const videos = this.track.querySelectorAll("video");
    for (const v of videos) {
      v.pause();
    }
  }
}
